import React, { useRef, useState, useEffect } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, Input, NoticeBox, Tags } from "../../ui";
import { reviewChangeRequestAPI } from "../../../utils/api/applications";
import { errorMessage } from "./applicationsText";
import { BRANCH_TAG, REQUEST_TYPE_TAG } from "./applicationsModel";

const dash = (value) => (String(value || "").trim() ? String(value) : "—");

// Administrator review of one integration / activation / deactivation request:
// what would change per branch, current vs proposed information, then approve
// or reject (with a reason). The decision quotes the version that was reviewed;
// the backend refuses it when the request was edited meanwhile.
export default function ReviewRequestModal({ open, request, t, token, onClose, onDone, onStale }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setRejecting(false);
    setReason("");
    setError("");
  }, [request?.id, request?.version]);

  if (!open || !request) return null;
  const r = t.reviewModal;
  const { proposed, current } = request;

  // Rows of "field / current / proposed"; a row is highlighted when it changes.
  const rows = [];
  if (proposed.account) {
    rows.push([t.integrate.accountId, current.account?.id, proposed.account.id]);
    rows.push([t.integrate.accountEmail, current.account?.email, proposed.account.email]);
    rows.push([r.password, r.passwordUnchanged, proposed.account.passwordChanged ? r.passwordChanged : r.passwordUnchanged]);
  }
  if (proposed.manager) {
    rows.push([t.integrate.managerName, current.manager?.name, proposed.manager.name]);
    rows.push([t.integrate.managerPhone, current.manager?.phone, proposed.manager.phone]);
    rows.push([t.integrate.managerEmail, current.manager?.email, proposed.manager.email]);
  }
  request.changes.forEach((change) => {
    const live = current.branches.find((item) => item.branchId === change.branchId) || {};
    const next = proposed.branches.find((item) => item.branchId === change.branchId);
    if (next) {
      rows.push([`${change.name} · ${t.integrate.appId}`, live.appId, next.appId]);
      rows.push([`${change.name} · ${t.integrate.appName}`, live.appName, next.appName]);
    }
  });

  const decide = async (decision) => {
    setBusy(true);
    setError("");
    try {
      await reviewChangeRequestAPI(token, request.id, { decision, version: request.version, ...(decision === "reject" ? { reason: reason.trim() } : {}) });
      onDone?.(t.manage.done, "success");
    } catch (e) {
      if (!alive.current) return;
      if (e.code === "STALE_REVIEW" || e.code === "NOT_PENDING") onStale?.(r.stale);
      else setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <LudDashboardModal open title={r.title(t.requestTypes[request.type])} size="lg" onClose={onClose} busy={busy} footer={<Button variant="neutral" appearance="soft" onClick={onClose} disabled={busy}>{t.close}</Button>}>
      <div className="lud-app-stack">
        <div className="lud-app-summary">
          <Tags variant={REQUEST_TYPE_TAG[request.type]} appearance="soft">{t.requestTypes[request.type]}</Tags>
          <strong dir="ltr">{request.reference}</strong>
          <small>{t.changes.version(request.version)}</small>
          <small>{`${t.changes.by}: ${dash(request.requestedBy)}`}</small>
        </div>
        {error && <NoticeBox variant="danger" size="sm">{error}</NoticeBox>}
        {request.scope === "store" && <NoticeBox variant="warning" size="sm">{r.wholeApp}</NoticeBox>}
        {request.reason && (
          <NoticeBox variant="warning" size="sm" title={r.deactivationReason}>
            <span className="lud-app-reason-text">{request.reason}</span>
          </NoticeBox>
        )}

        <section>
          <h4>{r.statusChange}</h4>
          <ul className="lud-app-branch-list">
            {request.changes.map((change) => (
              <li key={change.branchId}>
                <strong>{change.name}</strong>
                <span className="lud-app-tags">
                  <Tags variant={BRANCH_TAG[change.from] || "neutral"} appearance="soft" size="sm">{t.status[change.from] || change.from}</Tags>
                  <span aria-hidden="true">→</span>
                  <Tags variant={BRANCH_TAG[change.to] || "neutral"} appearance="soft" size="sm">{t.status[change.to] || change.to}</Tags>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {rows.length > 0 && (
          <section>
            <div className="lud-app-table-wrap">
              <table className="lud-app-table">
                <thead>
                  <tr><th />
                    <th>{r.current}</th>
                    <th>{r.proposed}</th></tr>
                </thead>
                <tbody>
                  {rows.map(([label, from, to], index) => {
                    const changed = String(from || "") !== String(to || "");
                    return (
                      <tr key={`${label}-${index}`} className={changed ? "lud-app-changed" : ""}>
                        <td>{label}</td>
                        <td dir="auto">{dash(from)}</td>
                        <td dir="auto">{changed ? <strong>{dash(to)}</strong> : r.unchanged}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <details className="lud-app-history">
          <summary>{r.history}</summary>
          <ul>
            {request.history.map((item, index) => (
              <li key={index}>
                <strong>{item.action}</strong> v{item.version ?? ""} {item.by ? `· ${item.by}` : ""} {item.note ? `· ${item.note}` : ""}
              </li>
            ))}
          </ul>
        </details>

        <div className="lud-app-actions">
          <Button variant="success" appearance="filled" disabled={busy} onClick={() => decide("approve")}>{r.approve}</Button>
          <Button variant="danger" appearance="soft" disabled={busy} onClick={() => setRejecting((value) => !value)}>{r.reject}</Button>
        </div>
        {rejecting && (
          <div className="lud-app-reject">
            <Input textarea label={r.reason} value={reason} onChange={(event) => setReason(event.target.value)} required />
            <Button variant="danger" appearance="filled" disabled={busy || reason.trim().length < 3} onClick={() => decide("reject")}>{r.reject}</Button>
          </div>
        )}
      </div>
    </LudDashboardModal>
  );
}
